/* What a composer holds and hasn't sent, kept while the person is elsewhere in the app: they
   open Settings mid-sentence and come back to it. Both apps use it (the consumer app reads
   `@/shared` from here); `useDraft` (`@/shared/hooks/useDraft`) is the hook over it.

   Drafts are the signed-in account's. The store is pointed at it once the session is known
   (`draftsFor`, as the avatar store is), and every key is that account's: another account on
   the same tab reads none of them, and a session that ended gives its person their drafts
   back once they sign in again. Signing out on purpose drops them all (`clearDrafts`). Until
   the store is pointed at someone, nothing is kept.

   Anything that turns into JSON (words, a quote, chips, an uploaded file's id) goes in
   sessionStorage, so a reload keeps it and closing the tab drops it. Picked `File`s can't, so
   they are kept in memory for the page's life (`keptFiles`). Storage can be missing or refuse
   (a private window, a full quota): every read and write is guarded, and a composer without it
   simply starts empty. An entry left with nothing in it is removed. */

const PREFIX = "draft:";
/** The signed-in account the drafts belong to; "" until the session is known. */
let owner = "";
/** Picked files, under the same owner-scoped keys as the stored drafts. */
const files = new Map<string, unknown[]>();

const slot = (key: string) => `${PREFIX}${owner}:${key}`;

/** Point the store at the signed-in account; "" when nobody is. Idempotent: called in render
    on every session answer. */
export function draftsFor(userId: string): void {
  owner = userId;
}

/** Whose drafts are kept now: a send's answer that comes back for someone else touches nothing. */
export const draftOwner = (): string => owner;

/** Nothing to keep: no words, no quote, no chips. */
const blank = (value: unknown) => value == null || value === "" || (Array.isArray(value) && value.length === 0);

/** The draft kept under `key`; `{}` when there is none or storage can't be read. */
export function readDraft<T extends object>(key: string): Partial<T> {
  if (!owner) return {};
  try {
    const saved = JSON.parse(sessionStorage.getItem(slot(key)) ?? "{}");
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  } catch {
    return {};
  }
}

/** Keeps `draft` under `key`, in place of what was there; fields with nothing in them are left out. */
export function saveDraft<T extends object>(key: string, draft: T): void {
  if (!owner) return;
  const kept = Object.fromEntries(Object.entries(draft).filter(([, value]) => !blank(value)));
  try {
    if (Object.keys(kept).length) sessionStorage.setItem(slot(key), JSON.stringify(kept));
    else sessionStorage.removeItem(slot(key));
  } catch {
    // no storage: the draft lasts as long as the composer does
  }
}

/** The picked files waiting under `key`. */
export const keptFiles = <T,>(key: string): T[] => (owner ? ((files.get(slot(key)) as T[] | undefined) ?? []) : []);

export function keepFiles(key: string, picked: unknown[]): void {
  if (!owner) return;
  if (picked.length) files.set(slot(key), picked);
  else files.delete(slot(key));
}

/** Frees the previews (`blob:` URLs) of files that are thrown away rather than sent: a sent
    file's preview still draws its message. */
export function freePreviews(picked: unknown[]): void {
  for (const file of picked) {
    const preview = (file as { previewSrc?: unknown } | null)?.previewSrc;
    if (typeof preview === "string" && preview.startsWith("blob:")) URL.revokeObjectURL?.(preview);
  }
}

/** Throws the draft under `key` away, previews and all. */
export function discardDraft(key: string): void {
  freePreviews(keptFiles(key));
  keepFiles(key, []);
  saveDraft(key, {});
}

/** Every draft in this tab, whoever's, on signing out: the next person sees none of them. */
export function clearDrafts(): void {
  files.forEach(freePreviews);
  files.clear();
  owner = "";
  try {
    Object.keys(sessionStorage).filter((key) => key.startsWith(PREFIX)).forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // no storage: nothing was kept there
  }
}
